import { z } from 'zod';
import { Dateish, intList } from './common';

// ── Queries ─────────────────────────────────────────────────────────────────
/** Período obrigatório (ISO date YYYY-MM-DD) — todos os relatórios exceto workload. */
export const ReportPeriodQuerySchema = z.object({
  start: z.string().date(),
  end: z.string().date(),
});
export type ReportPeriodQuery = z.infer<typeof ReportPeriodQuerySchema>;

/**
 * Recorte pelo CLIENTE (pedido da PoolTrack, 02/10: "separar cliente/associação
 * e atendente"). Vale em todo relatório cujo dado vem de ticket: o cliente é o
 * `tickets.client_id`; nas conversas do relatório de atendentes, o cliente da
 * própria conversa e, sem ele, o do ticket vinculado. Mesmo contrato do
 * `user_ids`: ausente ou vazio = todos; id desconhecido (ou de outro tenant)
 * não casa nada; acima de 15 ids, 422.
 */
export const ClientFilterQuerySchema = z.object({
  client_ids: intList.optional(),
});

/** GET /reports/appointments-extract */
export const AppointmentsExtractQuerySchema = ReportPeriodQuerySchema.extend({
  user_ids: intList.optional(),
  desk_ids: intList.optional(),
  ...ClientFilterQuerySchema.shape,
  /**
   * Paginação OPCIONAL das linhas (mesmos limites do PaginationQuerySchema).
   * Sem limit e sem offset a resposta é a de sempre, com todas as linhas — é
   * o que o SPA publicado antes da paginação pede, e o que a exportação pede.
   * Com um dos dois, `rows` é a página e a resposta ganha `rowsTotal`.
   */
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
  /**
   * `pdf` (PoolTrack, 03/10: "baixar em PDF o relatório de cada atendente"):
   * o MESMO filtro, a MESMA rota e a MESMA permissão, servidos como documento —
   * um atendente por página nova, com o total dele. O PDF é sempre o filtro
   * inteiro: com limit/offset é 422, nunca "a página 2 em PDF" calada.
   */
  format: z.enum(['json', 'pdf']).default('json'),
}).superRefine((q, ctx) => {
  if (q.format === 'pdf' && (q.limit !== undefined || q.offset !== undefined)) {
    ctx.addIssue({
      code: 'custom',
      path: ['format'],
      message: 'o PDF cobre o filtro inteiro: sem limit/offset',
    });
  }
});
export type AppointmentsExtractQuery = z.infer<typeof AppointmentsExtractQuerySchema>;

/** GET /reports/workload — foto de agora: sem período, só o recorte de cliente */
export const WorkloadQuerySchema = ClientFilterQuerySchema;

/** GET /reports/peaks e /reports/catalog */
export const PeaksQuerySchema = ReportPeriodQuerySchema.extend(ClientFilterQuerySchema.shape);
export const CatalogReportQuerySchema = PeaksQuerySchema;

/** GET /reports/executive e /reports/ratings */
export const DeskFilteredReportQuerySchema = ReportPeriodQuerySchema.extend({
  desk_ids: intList.optional(),
  ...ClientFilterQuerySchema.shape,
});
export type DeskFilteredReportQuery = z.infer<typeof DeskFilteredReportQuerySchema>;

// ── Opções dos filtros ──────────────────────────────────────────────────────
/**
 * GET /reports/filter-options — o que os seletores da tela de relatórios
 * oferecem. `attendants` é só a EQUIPE do tenant (admin e atendente), nunca o
 * usuário do portal do cliente (role `client`); `clients` são os clientes do
 * tenant. `active` deixa a tela esconder o inativo que ninguém marcou.
 */
export const ReportFilterOptionSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  active: z.boolean(),
});
export type ReportFilterOption = z.infer<typeof ReportFilterOptionSchema>;

export const ReportFilterOptionsSchema = z.object({
  attendants: z.array(ReportFilterOptionSchema),
  clients: z.array(ReportFilterOptionSchema),
});
export type ReportFilterOptions = z.infer<typeof ReportFilterOptionsSchema>;

// ── 1. Extrato de apontamentos ──────────────────────────────────────────────
export const AppointmentsExtractRowSchema = z.object({
  userId: z.number().int(),
  userName: z.string(),
  ticketNumber: z.number().int(),
  ticketTitle: z.string(),
  deskName: z.string(),
  startedAt: Dateish,
  /** null = timer ainda rodando */
  endedAt: Dateish.nullable(),
  minutes: z.number().int(),
});
export type AppointmentsExtractRow = z.infer<typeof AppointmentsExtractRowSchema>;

export const AppointmentsExtractReportSchema = z.object({
  /** todas as linhas do filtro; com limit/offset, só a página pedida */
  rows: z.array(AppointmentsExtractRowSchema),
  /** só na resposta paginada: quantas linhas o filtro tem ao todo */
  rowsTotal: z.number().int().optional(),
  /** os totais cobrem SEMPRE o filtro inteiro, paginado ou não */
  totalsByUser: z.array(
    z.object({
      userId: z.number().int(),
      userName: z.string(),
      totalMinutes: z.number().int(),
    })
  ),
  totalMinutes: z.number().int(),
});
export type AppointmentsExtractReport = z.infer<typeof AppointmentsExtractReportSchema>;

/**
 * Teto de linhas do extrato em PDF. Acima dele a API responde 422
 * `extract_pdf_too_large` ({rows, max}) e a tela pede um recorte menor (um
 * atendente, um período mais curto) — o CSV não tem teto.
 *
 * Medido (03/10, ~1 ms por linha): 2.000 linhas = ~2,2 s e ~400 páginas; com
 * 5.000 a memória do processo passou de 500 MB, e o pod da API tem limite de
 * 768 Mi — um PDF grande derrubaria o pod com as requests de todo mundo nele.
 * O extrato de UM atendente num mês fica na casa das centenas.
 */
export const EXTRACT_PDF_MAX_ROWS = 2000;

/** 90 → "1:30": horas sem zero à esquerda, minutos com dois dígitos (tela, CSV e PDF) */
export function minutesToHmm(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

/** "José Antônio Gonçalves" → "jose-antonio-goncalves": nome de arquivo sem acento nem espaço */
export function fileNameSlug(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, 60)
    .replace(/-+$/, '');
}

/**
 * Nome do extrato em PDF — a API (Content-Disposition) e a tela (o arquivo
 * salvo) montam pelo MESMO caminho: `extrato-<atendente>-<início>-<fim>.pdf`
 * quando o documento é de UMA pessoa, `extrato-apontamentos-<início>-<fim>.pdf`
 * quando é de várias (ou de nenhuma).
 */
export function extractPdfFileName(input: {
  start: string;
  end: string;
  attendantName?: string | null;
}): string {
  const who = input.attendantName ? fileNameSlug(input.attendantName) : '';
  return `extrato-${who || 'apontamentos'}-${input.start}-${input.end}.pdf`;
}

// ── 2. Carga de trabalho (foto de agora; sem período) ───────────────────────
export const WorkloadRowSchema = z.object({
  /** null = linha "sem responsável" */
  userId: z.number().int().nullable(),
  userName: z.string(),
  openTickets: z.number().int(),
  runningTimers: z.number().int(),
  minutesLast7d: z.number().int(),
});
export type WorkloadRow = z.infer<typeof WorkloadRowSchema>;

export const WorkloadReportSchema = z.object({
  rows: z.array(WorkloadRowSchema),
});
export type WorkloadReport = z.infer<typeof WorkloadReportSchema>;

// ── 3. Picos de abertura ────────────────────────────────────────────────────
export const PeaksReportSchema = z.object({
  /** matrix[weekday][hour] = tickets criados; weekday 0=dom … 6=sáb, hour 0–23 */
  matrix: z.array(z.array(z.number().int()).length(24)).length(7),
  /** total por dia da semana (mesma ordem da matriz) */
  totalsByWeekday: z.array(z.number().int()).length(7),
  total: z.number().int(),
});
export type PeaksReport = z.infer<typeof PeaksReportSchema>;

// ── 4. Executivo (por mesa) ─────────────────────────────────────────────────
const ExecutiveMetricsSchema = z.object({
  created: z.number().int(),
  closed: z.number().int(),
  canceled: z.number().int(),
  /** média (h) entre criação e fechamento dos fechados no período; null = sem fechados */
  avgSolveHours: z.number().nullable(),
  /** % cumprido sobre tickets COM meta no período; null = nenhum com meta */
  slaAttendMetPct: z.number().nullable(),
  slaSolveMetPct: z.number().nullable(),
});

export const ExecutiveRowSchema = ExecutiveMetricsSchema.extend({
  deskId: z.number().int(),
  deskName: z.string(),
});
export type ExecutiveRow = z.infer<typeof ExecutiveRowSchema>;

/**
 * Fechados no período por mesa e motivo (spec 054). `reasonId` null = fechado
 * sem motivo (mesa sem motivo, TiFlux, ou antes da spec 054). Só as mesas do
 * relatório, e só combinações com pelo menos um fechado.
 */
export const ExecutiveCloseReasonRowSchema = z.object({
  deskId: z.number().int(),
  deskName: z.string(),
  reasonId: z.number().int().nullable(),
  reasonName: z.string().nullable(),
  closed: z.number().int(),
});
export type ExecutiveCloseReasonRow = z.infer<typeof ExecutiveCloseReasonRowSchema>;

export const ExecutiveReportSchema = z.object({
  rows: z.array(ExecutiveRowSchema),
  totals: ExecutiveMetricsSchema,
  /** ausente = API anterior à spec 054 */
  closeReasons: z.array(ExecutiveCloseReasonRowSchema).optional(),
});
export type ExecutiveReport = z.infer<typeof ExecutiveReportSchema>;

// ── 5. Catálogo (tickets por catálogo → área → item) ────────────────────────
export const CatalogReportRowSchema = z.object({
  catalogId: z.number().int(),
  catalogName: z.string(),
  areaId: z.number().int(),
  areaName: z.string(),
  itemId: z.number().int(),
  itemName: z.string(),
  tickets: z.number().int(),
});
export type CatalogReportRow = z.infer<typeof CatalogReportRowSchema>;

export const CatalogReportSchema = z.object({
  rows: z.array(CatalogReportRowSchema),
  /** tickets criados no período SEM item de catálogo (fora das linhas acima) */
  withoutCatalog: z.number().int(),
  total: z.number().int(),
});
export type CatalogReport = z.infer<typeof CatalogReportSchema>;

// ── 6. Avaliações (CSAT) ────────────────────────────────────────────────────
export const RatingsReportSchema = z.object({
  /** pesquisas enviadas no período */
  sent: z.number().int(),
  responses: z.number().int(),
  /** média das notas; null = sem respostas */
  average: z.number().nullable(),
  /** distribution[i] = quantidade da nota i+1 (1..5) */
  distribution: z.array(z.number().int()).length(5),
  /** respostas/enviadas × 100; null = nada enviado */
  responseRatePct: z.number().nullable(),
  recent: z.array(
    z.object({
      ticketNumber: z.number().int(),
      ticketTitle: z.string(),
      score: z.number().int(),
      comment: z.string().nullable(),
      respondedAt: Dateish,
    })
  ),
});
export type RatingsReport = z.infer<typeof RatingsReportSchema>;

// ── 7. Atendentes (spec 015) ────────────────────────────────────────────────
/** GET /reports/attendants */
export const AttendantsReportQuerySchema = ReportPeriodQuerySchema.extend({
  user_ids: intList.optional(),
  ...ClientFilterQuerySchema.shape,
});
export type AttendantsReportQuery = z.infer<typeof AttendantsReportQuerySchema>;

export const AttendantsReportRowSchema = z.object({
  userId: z.number().int(),
  userName: z.string(),
  /**
   * minutos em "Disponível" (o mesmo log de status da pausa, recortado no
   * período) — o "tempo online" que a PoolTrack pediu em 08/10. Sai com o
   * filtro de cliente, como a pausa (`pausesIncluded`). `default(0)`: a API
   * anterior não manda o campo.
   */
  availableMinutes: z.number().int().default(0),
  /** soma dos minutos por motivo (a coluna "Pausa" bate com as parciais) */
  pauseMinutes: z.number().int(),
  /** minutos por motivo, na ordem de `reasons` do relatório; motivo sem pausa = 0 */
  pauseMinutesByReason: z.array(z.number().int()),
  /** existe pausa ainda ABERTA contada até agora */
  pauseOpen: z.boolean(),
  /** minutos com conversa na mão (atribuições recortadas no período) */
  chatMinutes: z.number().int(),
  /** conversas distintas que ele teve em algum momento do período */
  chatsAttended: z.number().int(),
  /** conversas encerradas por ele no período */
  chatsClosed: z.number().int(),
  /** tickets distintos com evento ou apontamento dele no período */
  ticketsWorked: z.number().int(),
  /** tickets distintos fechados por ele no período */
  ticketsClosed: z.number().int(),
  /** apontamentos finalizados iniciados no período (mesma regra do extrato) */
  appointmentMinutes: z.number().int(),
});
export type AttendantsReportRow = z.infer<typeof AttendantsReportRowSchema>;

export const AttendantsReportSchema = z.object({
  /** motivos que aparecem no período, na ordem configurada — colunas da tabela */
  reasons: z.array(z.object({ id: z.number().int(), name: z.string() })),
  /**
   * false com o filtro de cliente: pausa não é de cliente nenhum, então as
   * colunas de pausa não se aplicam (vêm zeradas e sem motivos) e a tela as
   * esconde em vez de mostrar "0,00" como se fosse medida.
   */
  pausesIncluded: z.boolean(),
  rows: z.array(AttendantsReportRowSchema),
});
export type AttendantsReport = z.infer<typeof AttendantsReportSchema>;
